import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { toast } from 'react-toastify';
import ScheduledReportsTab from '../components/settings/ScheduledReportsTab';
import { Campaign } from '../types';

const readOnlyField = { readOnly: true };

const NotificationSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testSmsRecipient, setTestSmsRecipient] = useState('');
  const [testWhatsAppRecipient, setTestWhatsAppRecipient] = useState('');
  const [loading, setLoading] = useState(false);

  const isAdmin = user && ['administrator', 'super_user'].includes(user.role);
  const canViewSchedules = user && (
    ['administrator', 'super_user', 'manager'].includes(user.role)
    || (user.permissions || []).includes('settings.read')
  );
  const canManageSchedules = !!isAdmin;
  const showDeliveryTab = !!isAdmin;
  const showSchedulesTab = !!canViewSchedules;

  useEffect(() => {
    if (isAdmin || canViewSchedules) {
      load();
      apiClient.campaigns.getAll().then((res) => setCampaigns(res.data.data || [])).catch(() => setCampaigns([]));
    }
  }, [isAdmin, canViewSchedules]);

  const load = async () => {
    if (isAdmin) {
      const response = await apiClient.notificationSettings.get();
      setSettings(response.data.data);
    }
  };

  const save = async () => {
    try {
      setLoading(true);
      const response = await apiClient.notificationSettings.update({
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpSecure: settings.smtpSecure,
        smtpUser: settings.smtpUser,
        smtpFrom: settings.smtpFrom,
        smtpPassword: settings.smtpPassword,
        isSmsEnabled: settings.isSmsEnabled,
        isWhatsappEnabled: settings.isWhatsappEnabled,
        twilioAccountSid: settings.twilioAccountSid,
        twilioAuthToken: settings.twilioAuthToken,
        twilioFromNumber: settings.twilioFromNumber,
        twilioMessagingServiceSid: settings.twilioMessagingServiceSid,
        twilioWhatsappFromNumber: settings.twilioWhatsappFromNumber,
      });
      setSettings(response.data.data);
      toast.success('Notification settings saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin && !canViewSchedules) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">You do not have permission to view notification settings.</Alert>
      </Container>
    );
  }

  if (isAdmin && !settings) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const emailConfig = settings?.emailConfig;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Notification Settings</Typography>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        {showSchedulesTab && <Tab label="Scheduled Reports" />}
        {showDeliveryTab && <Tab label="Email, SMS & WhatsApp" />}
      </Tabs>

      {showSchedulesTab && tab === 0 && (
        <ScheduledReportsTab canManage={canManageSchedules} campaigns={campaigns} />
      )}

      {showDeliveryTab && tab === (showSchedulesTab ? 1 : 0) && settings && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Email Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              SMTP settings for staffing alerts and scheduled email. Values are saved here and used for delivery.
              Server <code>SMTP_*</code> environment variables are used as fallback when a field is left empty.
            </Typography>

            <Alert severity={emailConfig?.configured ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {emailConfig?.configured
                ? `${emailConfig.provider} email is configured and ready.`
                : 'SMTP is not fully configured — set host, user, and password below (or SMTP_* in server .env).'}
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Provider" value={emailConfig?.provider || ''} InputProps={readOnlyField} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Configuration source" value={emailConfig?.source || ''} InputProps={readOnlyField} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={settings.smtpHost || ''}
                  onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                  placeholder="smtp.office365.com"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  value={settings.smtpPort || ''}
                  onChange={(e) => setSettings({ ...settings, smtpPort: e.target.value })}
                  placeholder="587"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Encryption" value={emailConfig?.encryption || ''} InputProps={readOnlyField} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={!!settings.smtpSecure}
                      onChange={(e) => setSettings({ ...settings, smtpSecure: e.target.checked })}
                    />
                  )}
                  label="Use SSL/TLS (typically port 465). Leave off for STARTTLS on port 587."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP User"
                  value={settings.smtpUser || ''}
                  onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Address"
                  value={settings.smtpFrom || ''}
                  onChange={(e) => setSettings({ ...settings, smtpFrom: e.target.value })}
                  helperText="Usually the same as SMTP user for Office 365."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="SMTP Password"
                  placeholder={settings.smtpPasswordConfigured ? 'Saved (enter new to replace)' : ''}
                  value={settings.smtpPassword || ''}
                  onChange={(e) => setSettings({ ...settings, smtpPassword: e.target.value })}
                  helperText={settings.smtpPasswordConfigured ? 'Password is saved (not shown).' : 'Required for sending email.'}
                />
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label="Test email recipient"
                  value={testEmailRecipient}
                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                />
                <Button sx={{ mt: 1 }} onClick={() => apiClient.notificationSettings.testEmail(testEmailRecipient).then(() => toast.success('Test email sent')).catch((e) => toast.error(e?.response?.data?.message || 'Failed'))}>
                  Send test email
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>SMS Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Twilio settings for staffing alerts and scheduled SMS. Can also be set via <code>TWILIO_*</code> in the server environment.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={!!settings.isSmsEnabled} onChange={(e) => setSettings({ ...settings, isSmsEnabled: e.target.checked })} />}
                  label="SMS enabled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Twilio Account SID" value={settings.twilioAccountSid || ''} onChange={(e) => setSettings({ ...settings, twilioAccountSid: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="password" label="Twilio Auth Token" placeholder={settings.twilioAuthTokenConfigured ? 'Saved (enter new value to replace)' : ''} value={settings.twilioAuthToken || ''} onChange={(e) => setSettings({ ...settings, twilioAuthToken: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Twilio From Number" helperText="E.164 format, e.g. +12125551234. Optional if Messaging Service SID is set." value={settings.twilioFromNumber || ''} onChange={(e) => setSettings({ ...settings, twilioFromNumber: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Twilio Messaging Service SID" helperText="Optional. Can also set TWILIO_* in server environment (same as NetDealer)." value={settings.twilioMessagingServiceSid || ''} onChange={(e) => setSettings({ ...settings, twilioMessagingServiceSid: e.target.value })} />
              </Grid>
              {settings.smsConfigured === false && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Twilio is not fully configured. Set credentials here or via TWILIO_* environment variables on the server.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Test SMS recipient" value={testSmsRecipient} onChange={(e) => setTestSmsRecipient(e.target.value)} />
                <Button sx={{ mt: 1 }} onClick={() => apiClient.notificationSettings.testSms(testSmsRecipient).then(() => toast.success('Test SMS sent')).catch((e) => toast.error(e?.response?.data?.message || 'Failed'))}>
                  Send test SMS
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>WhatsApp Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Uses the same Twilio account. For sandbox testing, set sender to <code>whatsapp:+14155238886</code>
              and have each recipient join the sandbox from WhatsApp first.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={!!settings.isWhatsappEnabled} onChange={(e) => setSettings({ ...settings, isWhatsappEnabled: e.target.checked })} />}
                  label="WhatsApp enabled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Twilio WhatsApp From Number"
                  helperText="Sandbox example: whatsapp:+14155238886"
                  value={settings.twilioWhatsappFromNumber || ''}
                  onChange={(e) => setSettings({ ...settings, twilioWhatsappFromNumber: e.target.value })}
                />
              </Grid>
              {settings.whatsappConfigured === false && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    WhatsApp is not fully configured. Set Twilio credentials above and the WhatsApp sender number.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Test WhatsApp recipient" placeholder="+16822063656" value={testWhatsAppRecipient} onChange={(e) => setTestWhatsAppRecipient(e.target.value)} />
                <Button sx={{ mt: 1 }} onClick={() => apiClient.notificationSettings.testWhatsApp(testWhatsAppRecipient).then(() => toast.success('Test WhatsApp sent')).catch((e) => toast.error(e?.response?.data?.message || 'Failed'))}>
                  Send test WhatsApp
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={save} disabled={loading}>{loading ? 'Saving...' : 'Save Settings'}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default NotificationSettingsPage;
